<template>
  <div class="modal-backdrop" @click.self="$emit('close')">
    <div class="modal-card">
      <header>
        <h3>Upload retention image</h3>
        <button class="icon-btn" @click="$emit('close')" type="button" aria-label="Close">
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="body">
        <div class="form-row">
          <label>Name <span class="required">*</span></label>
          <input
            v-model="name"
            placeholder="Internal label"
            class="text-input"
            type="text"
          />
        </div>

        <div class="form-row">
          <label>Description <span class="optional">(optional)</span></label>
          <textarea
            v-model="description"
            :maxlength="DESCRIPTION_MAX"
            rows="2"
            placeholder="Where/why this art is used (max 200 chars)"
            class="text-input"
          ></textarea>
          <span class="char-count">{{ description.length }}/{{ DESCRIPTION_MAX }}</span>
        </div>

        <div class="form-row">
          <label>How will this image be used? <span class="required">*</span></label>
          <div class="usage-radio-group">
            <label class="usage-radio">
              <input type="radio" v-model="usage" value="main" />
              <span class="usage-radio-label">
                <strong>Main image</strong>
                <span class="usage-radio-hint">
                  Hero banner above/below the message text. Use a wide
                  16:9 image — taller or narrower sources get white bands.
                </span>
              </span>
            </label>
            <label class="usage-radio">
              <input type="radio" v-model="usage" value="bullet" />
              <span class="usage-radio-label">
                <strong>Bullet icon</strong>
                <span class="usage-radio-hint">
                  Small square icon next to a bullet point. Use a tightly
                  cropped square — non-square sources get white edges.
                </span>
              </span>
            </label>
          </div>
        </div>

        <div
          class="dropzone"
          :class="{ dragover: dragover }"
          @click="$refs.fileInput.click()"
          @dragover.prevent="dragover = true"
          @dragleave.prevent="dragover = false"
          @drop.prevent="onDrop"
        >
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            @change="onFileChange"
            hidden
          />
          <div v-if="!previewUrl" class="dropzone-empty">
            <i class="fas fa-cloud-upload-alt fa-2x"></i>
            <p>Click to choose an image, or drag-and-drop here.</p>
          </div>
          <img v-else :src="previewUrl" alt="preview" class="preview" />
        </div>

        <div v-if="errors.length" class="errors">
          <p v-for="(err, i) in errors" :key="i">{{ err }}</p>
        </div>

        <label class="check">
          <input type="checkbox" v-model="submitImmediately" :disabled="!canSubmitRetentionImage" />
          Submit for Apple's review immediately after upload
          <span v-if="!canSubmitRetentionImage" class="hint inline">(no submit permission)</span>
        </label>
      </div>

      <footer>
        <button class="btn btn-secondary" type="button" @click="$emit('close')" :disabled="busy">
          Cancel
        </button>
        <button class="btn btn-primary" type="button" @click="upload" :disabled="!file || busy">
          <span v-if="busy" class="spinner-border spinner-border-sm"></span>
          <span v-else>Upload</span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { permissionsMixin } from '../../mixins/permissions.mixin';

const DESCRIPTION_MAX = 200;

export default {
  name: 'RetentionImageCreate',
  mixins: [permissionsMixin],
  data() {
    return {
      file: null,
      previewUrl: null,
      dragover: false,
      busy: false,
      submitImmediately: false,
      errors: [],
      name: '',
      description: '',
      usage: null,
      DESCRIPTION_MAX
    };
  },
  methods: {
    onFileChange(e) {
      const file = e.target.files?.[0];
      this.setFile(file);
    },
    onDrop(e) {
      this.dragover = false;
      const file = e.dataTransfer.files?.[0];
      this.setFile(file);
    },
    setFile(file) {
      this.errors = [];
      if (!file) return;
      if (!file.type || !file.type.startsWith('image/')) {
        this.errors.push('Please choose an image file.');
        return;
      }
      this.file = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    fileToBase64() {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          const base64 = result.split(',')[1] || result;
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(this.file);
      });
    },
    async upload() {
      if (!this.file) return;
      this.errors = [];
      if (!this.name.trim()) {
        this.errors.push('Name is required.');
        return;
      }
      if (this.description.length > DESCRIPTION_MAX) {
        this.errors.push(`Description must be ${DESCRIPTION_MAX} characters or fewer.`);
        return;
      }
      if (this.usage !== 'main' && this.usage !== 'bullet') {
        this.errors.push('Pick a usage — Main image or Bullet icon.');
        return;
      }
      this.busy = true;
      try {
        const base64 = await this.fileToBase64();
        const created = await this.$store.dispatch('retention/addImage', {
          image: base64,
          name: this.name.trim(),
          description: this.description.trim() || null,
          usage: this.usage
        });
        if (this.submitImmediately && this.canSubmitRetentionImage && created?.id) {
          await this.$store.dispatch('retention/submitImage', created.id);
        }
        this.$emit('created', created);
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Upload failed.';
        this.errors.push(msg);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(20, 30, 40, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  overflow-y: auto;
  z-index: 1000;
  animation: fade 0.15s ease;
}

@keyframes fade {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.modal-card {
  width: min(540px, 100%);
  margin: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

header, footer {
  display: flex;
  align-items: center;
  padding: 0.85rem 1.25rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  flex-shrink: 0;
}

footer {
  border-bottom: none;
  border-top: 1px solid #dee2e6;
  justify-content: flex-end;
  gap: 0.5rem;
}

header h3 {
  margin: 0;
  font-size: 1.05rem;
  color: #2c3e50;
  flex: 1;
}

.icon-btn {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;
}

.icon-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

.body {
  padding: 1.25rem;
}

.hint {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0 0 0.85rem;
}

.hint.inline {
  display: inline;
  margin: 0 0 0 0.4rem;
}

.form-row {
  margin-bottom: 0.85rem;
}

.form-row label {
  display: block;
  margin-bottom: 0.3rem;
  font-weight: 600;
  font-size: 0.85rem;
  color: #2c3e50;
}

.required {
  color: #e74c3c;
}

.optional {
  color: #95a5a6;
  font-weight: 400;
  font-size: 0.78rem;
}

.text-input {
  width: 100%;
  padding: 0.45rem 0.7rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #2c3e50;
  background: #fff;
  font-family: inherit;
}

.text-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
}

textarea.text-input {
  resize: vertical;
  min-height: 60px;
}

.char-count {
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: #95a5a6;
  margin-top: 0.2rem;
}

.usage-radio-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.2rem;
  width: 100%;
}

/* Override global black-dashboard label styles that pin width:auto and
   inline-block — radios need to stretch to the full form-row width. */
.usage-radio {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease;
  margin-bottom: 0;
  font-weight: 400;
  width: 100%;
  box-sizing: border-box;
}

.usage-radio:hover {
  border-color: #3498db;
  background: #f5fbff;
}

.usage-radio input[type="radio"] {
  margin-top: 0.18rem;
  cursor: pointer;
}

.usage-radio-label {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
}

.usage-radio-label strong {
  color: #2c3e50;
  font-size: 0.88rem;
  font-weight: 600;
}

.usage-radio-hint {
  color: #6c757d;
  font-size: 0.78rem;
  line-height: 1.3;
}


.dropzone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 1rem;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.dropzone.dragover,
.dropzone:hover {
  border-color: #3498db;
  background: #eef6fc;
}

.dropzone-empty p {
  margin: 0.5rem 0 0;
  color: #495057;
  font-size: 0.9rem;
}

.preview {
  max-width: 100%;
  max-height: 280px;
  border-radius: 6px;
}

.errors {
  margin-top: 0.75rem;
  color: #c0392b;
  font-size: 0.85rem;
}

.errors p {
  margin: 0.2rem 0;
}

.check {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 0.85rem;
  color: #495057;
}

.btn {
  padding: 0.45rem 1.1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:disabled {
  background: #a4cef0;
  cursor: not-allowed;
}

.btn-secondary {
  background: #fff;
  color: #34495e;
  border: 1px solid #dee2e6;
}

.btn-secondary:disabled {
  opacity: 0.6;
}
</style>
